import React, { useState, useEffect } from "react";
import {
  Input,
  Table,
  Card,
  CardBody,
  CardHeader,
  Button,
  Modal,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import {
  getResearchpaperApi,
  joinJournalApi,
  journalSearchApi,
  publisherJoinPaperApi,
} from "src/network/api";
import Pagination from "./Pagination";
import PageLayout from "../PageLayout";
import CountrySelector from "./CountrySelector";
import { toast } from "react-toastify";
import { useAppSelector } from "src/state/store/store";
import { IssuesSelector } from "src/utils/appUtils";

const JournalTable = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    country: "",
    publication_type: "",
    number_of_issue_per_year: "",
    review_type: "",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [researchPaper, setResearchPaper] = useState([]);
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const limit = 10;
  const user = useAppSelector((state) => state.auth.user);

  const [showModal, setShowModal] = useState(false);
  const [targetResearchPaper, setTargetResearchPaper] = useState(0);
  const [targetJournal, setTargetJournal] = useState(0);
  const handleFilterChange = (e: { target: { name: any; value: any } }) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const resetFilters = () => {
    setFilters({
      country: "",
      publication_type: "",
      number_of_issue_per_year: "",
      review_type: "",
    });
  };

  const handleModal = () => {
    setShowModal(false);
    setTargetJournal(0);
  };

  const handleJoinJournal = async (id) => {
    try {
      const response = await joinJournalApi(id);
      if (response.data.status === 200 && response.status === 200) {
        console.log(response.data.message);
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (e) {
      toast.error(e.response?.data?.message ?? e.message);
    }
  };

  const handleCountrySelect = (selectedCountry) => {
    setFilters({ ...filters, country: selectedCountry });
  };

  const fetchJournals = async (currentPage: number) => {
    const offset = currentPage;
    setLoading(true);
    setError("");
    try {
      const response = await journalSearchApi({
        limit,
        offset,
        name: searchQuery,
        country: filters.country || "",
        publication_type: filters.publication_type || "",
        number_of_issue_per_year: filters.number_of_issue_per_year || "",
        review_type: filters.review_type || "",
      });
      setJournals(response.data.data);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      setError("Failed to fetch journals. Please try again later.");
      console.error("Error fetching journals:", err);
      setJournals([]);
    } finally {
      setLoading(false);
    }
  };
  const fetchResearchPaper = async () => {
    try {
      const response = await getResearchpaperApi();
      console.log("API Response:", response);
      if (response?.data) {
        setResearchPaper(response.data);
      } else {
        console.error("No data received from API");
      }
    } catch (err) {
      console.error("Error fetching research papers:", err);
    }
  };

  useEffect(() => {
    fetchJournals(page);
  }, [filters, page, searchQuery]);

  useEffect(() => {
    fetchResearchPaper();
  }, []);

  return (
    <PageLayout>
      <div className="container-fluid journal-table">
      <div className="row align-items-center ml-4 ">
    {/* Search Bar */}
    <div className="col-md-2 p-1 ">
      <Input
        type="text"
        className="search-input"
        placeholder="Search"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
    </div>

    {/* Country Filter */}
    <div className="col-md-2 p-1">
      <CountrySelector
        currentCountry={filters.country}
        onSelectCountryCallback={handleCountrySelect}
      />
    </div>

    {/* Publication Type Filter */}
    <div className="col-md-2 p-1">
      <Input
        type="select"
        name="publication_type"
        id="publication_type"
        className="filter-input"
        value={filters.publication_type}
        onChange={handleFilterChange}
      >
        <option value="">--All Types--</option>
        <option value="Free">Free</option>
        <option value="Paid">Paid</option>
      </Input>
    </div>

    {/* Number of Issues Filter */}
    <div className="col-md-2 p-1 mt-3">
      <IssuesSelector
              name="number_of_issue_per_year"
              value={filters.number_of_issue_per_year}
              onChange={handleFilterChange}
              required={false}
              className="filter-input"
              control={undefined} label={undefined}      />
    </div>

    {/* Review Type Filter */}
    <div className="col-md-2 p-1">
      <Input
        type="select"
        name="review_type"
        value={filters.review_type}
        onChange={handleFilterChange}
        className="filter-input"
      >
        <option>-- Review Types--</option>
        <option value="Single-Blind">Single-Blind</option>
        <option value="Double-Blind">Double-Blind</option>
        <option value="Open Peer Review">Open Peer Review</option>
        <option value="Collaborative">Collaborative</option>
      </Input>
    </div>

    {/* Reset Filters Button */}
    <div className="col-md-1 p-0 ml-3">
      <button
        onClick={resetFilters}
        className="btn btn-danger w-100"
      >
        Reset
      </button>
    </div>
  </div>

        {/* Table Section */}
        <div
          className="table-container"
          style={{ overflowX: "auto", width: "100%" }}
        >
          <CardHeader className="bg-primary shadow-lg">
            <h3 className="mb-0 text-center font-weight-bold text-white">
              Journals List
            </h3>
          </CardHeader>

          {loading ? (
            <p className="text-center">Loading journals...</p>
          ) : error ? (
            <p className="text-danger">{error}</p>
          ) : journals.length > 0 ? (
            <div style={{ overflowX: "auto", width: "100%" }}>
              <Table bordered hover responsive className=" table-sm mb-0">
                <thead className="thead-light">
                  <tr>
                    <th>#</th>
                    <th>Journal Name</th>
                    <th>Image</th>
                    <th>E_ISSN</th>
                    <th>P_ISSN</th>
                    <th>Publisher Name</th>
                    <th>Research Area</th>
                    <th>Country</th>
                    <th>Publication Type</th>
                    <th>No of Issues</th>
                    <th>Review Type</th>
                    <th>Indexing</th>
                    <th>States</th>
                    <th>Join Journal</th>
                  
                  </tr>
                </thead>
                <tbody>
                  {journals.map((journal, index) => (
                    <tr key={index}>
                      <td>{index + 1 + (page - 1) * limit}</td>
                      <td>{journal.journal_name || "Unknown Title"}</td>
                      <td>
                        <img
                          src={journal.image}
                          alt={journal.image}
                          style={{ width: "100px", height: "100px" }}
                        />
                      </td>
                      <td>{journal.eissn_no || "N/A"}</td>
                      <td>{journal.pissn_no || "Not Specified"}</td>
                      <td>{journal.publisher_name || "N/A"}</td>
                      <td>{journal.broad_research_area || "N/A"}</td>
                      <td>{journal.country_name || "N/A"}</td>
                      <td>{journal.publication_type || "N/A"}</td>
                      <td>{journal.number_of_issue_per_year || "N/A"}</td>
                      <td>{journal.review_type || "N/A"}</td>
                      <td>{journal.indexing || "N/A"}</td>
                      <td>{journal.state_name || "N/A"}</td>

                      <td>
                        {user.type === "reviewer" ? (
                          <button
                            className="btn btn-primary"
                            onClick={() =>
                              handleJoinJournal(journal?.journal_id)
                            }
                          >
                            Join Journal
                          </button>
                        ) : user.type === "author" ? (
                          <button
                            className="btn btn-primary"
                            onClick={() => {
                              setShowModal(true);
                              setTargetJournal(journal?.journal_id);
                              console.log(
                                "Selected Journal ID:",
                                journal?.journal_id
                              ); 
                            }}
                          >
                            Publish Request
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <p
              style={{
                marginBottom: "80px",
                marginTop: "80px",
                textAlign: "center",
                fontSize: "18px",
                color: "#6c757d",
              }}
            >
              No journals found.
            </p>
          )}

           {researchPaper.length > 0 && (
            <div className="pagination-container">
              <Pagination total={totalPages} page={page} setPage={setPage} />
            </div>
          )}
          <Modal
            className="modal-dialog-centered"
            isOpen={showModal}
            toggle={() => setShowModal(!showModal)}
          >
            <div className="modal-header">
              <h6 className="modal-title" id="modal-title-default">
                Ask Author to Publish Paper In Your Journal
              </h6>
              <button
                aria-label="Close"
                className="close"
                data-dismiss="modal"
                type="button"
                onClick={handleModal}
              >
                <span aria-hidden={true}>×</span>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Choose a Paper</label>
                <Input
                  type="select"
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    
                    setTargetResearchPaper(value);
                  }}
                >
                  <option value={0}>Select Paper</option>
                  {researchPaper.map((researchPaper) => (
                    <option
                      key={researchPaper.paper_id}
                      value={researchPaper.paper_id}
                    >
                      {researchPaper.paper_title}
                    </option>
                  ))}
                </Input>
              </div>
            </div>
            <div className="modal-footer">
              <Button
                color="primary"
                type="button"
                disabled={targetJournal === 0 || targetResearchPaper === 0}
                onClick={async () => {
                  try {
                    const response = await publisherJoinPaperApi(
                      targetJournal,
                      targetResearchPaper
                    );

                    if (
                      response.status === 200 &&
                      response.data.status === 200
                    ) {
                      toast.success(response.data.message);
                    } else {
                      toast.error(response.data.message);
                    }
                  } catch (err) {
                    console.error("Error sending request:", err);
                    toast.error(err.response?.data?.message ?? err.message);
                  } finally {
                    setShowModal(false);
                  }
                }}
              >
                Send Request
              </Button>

              <Button
                className="ml-auto"
                color="link"
                data-dismiss="modal"
                type="button"
                onClick={() => setShowModal(false)}
              >
                Close
              </Button>
            </div>
          </Modal>
        </div>
      </div>
    </PageLayout>
  );
};

export default JournalTable;
